/**
 * React Query Persistence Setup
 * Configures IndexedDB for offline cache persistence
 */

import { PersistedClient, Persister } from '@tanstack/react-query-persist-client';
import { get, set, del } from 'idb-keyval';

/**
 * IndexedDB-backed persister for React Query
 * Stores query cache in IndexedDB for offline read access
 *
 * Cache Strategy:
 * - Persists all successful query results
 * - 24-hour max age for cached data
 * - Automatic cleanup of stale data
 * - Survives page refresh
 */
export function createIDBPersister(idbValidKey: IDBValidKey = 'reactQuery'): Persister {
  return {
    persistClient: async (client: PersistedClient) => {
      await set(idbValidKey, client);
    },
    restoreClient: async () => {
      return await get<PersistedClient>(idbValidKey);
    },
    removeClient: async () => {
      await del(idbValidKey);
    },
  };
}

/**
 * Cache configuration for offline persistence
 */
export const CACHE_CONFIG = {
  /** Maximum age of persisted cache (24 hours) */
  maxAge: 1000 * 60 * 60 * 24,

  /** IndexedDB key for query cache */
  cacheKey: 'auctus-query-cache',

  /** Dehydration options */
  dehydrateOptions: {
    // Only persist successful queries
    shouldDehydrateQuery: (query: any) => {
      return query.state.status === 'success';
    },
  },
};
